import { nanoid } from "nanoid";
import { isAcOnlyComponent } from "./ac";
import type { Category, ComponentDefinition, Terminal } from "./types";

/** Fields the inspector offers a per-field reset for. `terminals` is per-element;
 *  `id` is immutable. */
export const OVERRIDABLE_SCALAR_KEYS = [
  "name",
  "category",
  "width",
  "height",
  "capacityAh",
  "voltage",
  "watts",
  "ampOutput",
  "ampDraw",
  "ampSurge",
  "peakSunHoursDefault",
  "chargeAmpsTypical",
] as const satisfies readonly (keyof ComponentDefinition)[];

export type OverridableScalarKey = (typeof OVERRIDABLE_SCALAR_KEYS)[number];

/** Returns false when `originalDef` is missing — guard on it before showing a reset. */
export function isFieldOverridden<K extends OverridableScalarKey>(
  def: ComponentDefinition,
  originalDef: ComponentDefinition | null | undefined,
  key: K,
): boolean {
  if (!originalDef) return false;
  return def[key] !== originalDef[key];
}

/** Terminal fields with a per-field reset, against the catalog terminal of the same id. */
export const OVERRIDABLE_TERMINAL_KEYS = [
  "label",
  "polarity",
  "direction",
  "side",
  "offset",
  "maxAmp",
] as const satisfies readonly (keyof Terminal)[];

export type OverridableTerminalKey = (typeof OVERRIDABLE_TERMINAL_KEYS)[number];

/** Catalog terminal matched by id, or undefined if the terminal is user-added. */
export function originalTerminal(
  originalDef: ComponentDefinition | null | undefined,
  terminalId: string,
): Terminal | undefined {
  return originalDef?.terminals.find((t) => t.id === terminalId);
}

export function isTerminalFieldOverridden<K extends OverridableTerminalKey>(
  terminal: Terminal,
  original: Terminal | undefined,
  key: K,
): boolean {
  if (!original) return false;
  return terminal[key] !== original[key];
}

/** The spec fields the inspector offers a row for, in display order. */
export const SPEC_ROW_KEYS = [
  "capacityAh",
  "voltage",
  "watts",
  "ampOutput",
  "ampDraw",
  "ampSurge",
  "peakSunHoursDefault",
  "chargeAmpsTypical",
] as const satisfies readonly OverridableScalarKey[];

export type SpecRowKey = (typeof SPEC_ROW_KEYS)[number];

/** Fields a placeholder can fill from evidence. This is a list of empty
 * controls, not starter values: an absent rating must remain unknown. */
const PLACEHOLDER_SPEC_KEYS: Record<Category, readonly SpecRowKey[]> = {
  battery: ["capacityAh", "voltage"],
  charger: ["voltage", "ampOutput", "chargeAmpsTypical"],
  solar: ["voltage", "watts", "peakSunHoursDefault"],
  source: ["voltage", "ampOutput", "chargeAmpsTypical"],
  engine: ["voltage", "ampOutput", "ampDraw", "ampSurge"],
  distribution: [],
  load: ["voltage", "ampDraw", "ampSurge"],
  monitor: ["voltage", "ampDraw"],
};

/**
 * Which spec rows the inspector renders for `def`: every field it carries a
 * value for, plus every field its category is expected to carry even when the
 * value is missing. The second half is what lets a damaged definition be
 * repaired — a battery saved without `capacityAh` still shows an (empty)
 * capacity row instead of hiding the only control that could restore it.
 */
export function editableSpecKeys(
  def: Pick<ComponentDefinition, "category" | "loadCurrentModel" | "requestedModel" | SpecRowKey> &
    Partial<Pick<ComponentDefinition, "terminals">>,
): SpecRowKey[] {
  if (def.category === "load" && def.terminals && isAcOnlyComponent({ terminals: def.terminals })) {
    // AC appliances are excluded from the DC demand engine. Offer the AC
    // planning input instead of a current value that would have no effect.
    return ["watts"];
  }
  const expected = categorySpecDefaults(def.category);
  const placeholderKeys = def.requestedModel ? PLACEHOLDER_SPEC_KEYS[def.category] : [];
  if (def.category === "load" && def.loadCurrentModel === "constant-power") {
    // Power is the editable input. A reference ampDraw would have no effect.
    return SPEC_ROW_KEYS.filter((key) => key !== "ampDraw" &&
      (key === "watts" || def[key] != null || placeholderKeys.includes(key)));
  }
  return SPEC_ROW_KEYS.filter(
    (key) => def[key] != null || expected[key] != null || placeholderKeys.includes(key),
  );
}

/** Seed values for a category's expected spec fields; also the list of rows
 *  `editableSpecKeys` keeps visible when a definition lacks the value. A
 *  category with no seed here offers no spec controls unless the definition
 *  already carries a value. */
export function categorySpecDefaults(
  category: Category,
): Partial<ComponentDefinition> {
  switch (category) {
    case "battery":
      return { capacityAh: 100, voltage: 12 };
    case "charger":
      return { ampOutput: 30, voltage: 12 };
    case "solar":
      return { watts: 100, voltage: 18, peakSunHoursDefault: 4 };
    case "source":
      return { ampOutput: 50, voltage: 14 };
    case "load":
      return { ampDraw: 5 };
    case "engine":
    case "distribution":
    case "monitor":
      return {};
  }
}

/** Terminal rating (A) a from-scratch custom component starts at — a floor, not a claim. */
export const CUSTOM_TERMINAL_DEFAULT_MAX_AMP = 30;

/**
 * Derived, never declared by a model: `cable-current.ts` clamps to the smaller
 * endpoint `maxAmp`, so a guessed 30 A on a 250 A inverter lug silently sizes the
 * feed for 30 A. `customizeComponent` in `store.ts` keeps the same never-shrink rule.
 */
export function deriveTerminalMaxAmp(
  def: Pick<
    ComponentDefinition,
    "ampDraw" | "ampOutput" | "ampSurge" | "watts" | "voltage"
  >,
): number {
  const declared = [def.ampDraw, def.ampOutput, def.ampSurge]
    .filter((a): a is number => typeof a === "number" && Number.isFinite(a) && a > 0);

  // Watts and volts with no amps (solar, appliance nameplates) state it as P/V.
  if (
    declared.length === 0 &&
    typeof def.watts === "number" &&
    typeof def.voltage === "number" &&
    def.watts > 0 &&
    def.voltage > 0
  ) {
    declared.push(def.watts / def.voltage);
  }

  const peak = declared.length ? Math.max(...declared) : 0;
  return Math.max(CUSTOM_TERMINAL_DEFAULT_MAX_AMP, Math.ceil(peak));
}

/** `id: "custom"` is the sentinel that pairs with `ComponentNode.customDefinition`
 *  for inline storage; the catalog never holds this entry. */
export function buildCustomStub(
  category: Category = "load",
): ComponentDefinition {
  const positive: Terminal = {
    id: nanoid(8),
    label: "+",
    polarity: "positive",
    direction: "bidi",
    maxAmp: CUSTOM_TERMINAL_DEFAULT_MAX_AMP,
    side: "right",
    offset: 0.5,
  };
  const negative: Terminal = {
    id: nanoid(8),
    label: "−",
    polarity: "negative",
    direction: "bidi",
    maxAmp: CUSTOM_TERMINAL_DEFAULT_MAX_AMP,
    side: "left",
    offset: 0.5,
  };
  return {
    id: "custom",
    name: "Custom component",
    category,
    terminals: [positive, negative],
    width: 160,
    height: 100,
    ...categorySpecDefaults(category),
  };
}
